import type { IconType } from 'react-icons'
import { HiOutlineBookOpen, HiOutlineFolderOpen } from 'react-icons/hi2'
import {
  LuActivity,
  LuBookMarked,
  LuStickyNote,
  LuFileOutput,
  LuHome,
  LuQuote,
} from 'react-icons/lu'

import type { PdfSelectionPageRects } from '@/components/common/PdfViewerV2/PdfViewer'
import type { PresentationObjectContext as PresentationObjectContextState } from '@/components/office/PresentationViewer/viewerTypes'

import { getMemoryTargetFromExcerpt } from '@/helpers/memory'

import type {
  AgentName,
  FileNamespaceValue,
  PresentationObjectContext as PresentationObjectContextWire,
  PresentationObjectSelection,
  PresentationViewerState,
  PresentationOutlineFragment as PresentationOutlineFragmentWire,
  Source,
  ViewportContext,
  ViewportPlaybookDetail,
  ViewportViewerState,
} from '@/types/chat-v2'
import type { FileType, File as ServerFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type {
  PlaybookGraph,
  PlaybookRubric,
} from '@/types/library/PlaybookDefinition'
import type { PlaybookTab } from '@/types/library/PlaybookPlace'
import type {
  MemoryDetailTab,
  MemoryExcerpt,
  MemoryTarget,
} from '@/types/Memory'

// MARK: Tab navigation state

export interface TabNavState {
  view: 'list' | 'detail'
  fileId?: UUID
  fileNamespace?: FileNamespaceValue
  filePath?: string
  fileType?: FileType
  fileName?: string
  pdfPageScale?: number
  pdfPageScaleMode?: 'fixed' | 'fit-width'
  pdfScrollTop?: number
  /** 1-based page the viewer opens at; consumed once by FileViewer. */
  pdfInitialPage?: number
  /** Selection locator (selectionLocator.ts DSL — superset of the canonical
   * markit locators) the viewer should navigate/select to on open. Applied
   * whenever the activation key changes. */
  viewerInitialLocator?: string
  /** Captured viewer text to re-highlight on open. Re-applied with the locator
   * whenever the shared activation key changes. */
  viewerInitialHighlightText?: string
  viewerInitialLocatorKey?: number
  folderId?: UUID | null
  promptId?: UUID
  /** A library playbook the playbooks tab shows instead of a saved one, by slug. */
  librarySlug?: string
  /** The playbooks tab's list shows the Sweetspot library instead of the org's playbooks. */
  playbookLibrary?: boolean
  promptName?: string
  /** The slug playbook tools take, once the playbook or run is known (playbooks and playbook_runs tabs). */
  promptSlug?: string
  /** Tool-call id of the `enter` that started a playbook run (playbook_runs tab). */
  runId?: string
  /** Which view of the run the tab shows; its result when unset. */
  runView?: RunView
  /** The playbook page's tab (playbooks tab); its overview when unset. */
  playbookTab?: PlaybookTab
  /** The step opened on a playbook's or run's map. */
  mapStep?: string
  /** The plays picked on a playbook's map to change. */
  mapPicked?: string[]
  /** Counts the times plays were handed to the map, so the same plays handed again show again. */
  mapPicksHanded?: number
  /**
   * Where a run's Overview stands among its plays: a play, or null for the
   * run as a whole. Unset, the play under way while it runs, else the whole.
   */
  overviewStep?: string | null
  /** A run's step whose child playbook is shown before it runs. */
  childNode?: string
  mapStepLabel?: string
  /** The question opened on a playbook's or run's rubric. */
  rubricQuestion?: string
  rubricQuestionPrompt?: string
  memoryListScope?: 'organization' | 'pursuit'
  memoryTarget?: MemoryTarget
  /** The memory page's tab (memories tab); its details when unset. */
  memoryTab?: MemoryDetailTab
  draftToolCallId?: string
  activeSourceId?: number | string | null
  activeSourceSnapshot?: Source
  sourceActivationKey?: number
}

const RUN_VIEWS = ['result', 'map', 'rubric'] as const
export type RunView = (typeof RUN_VIEWS)[number]
export const isRunView = (value: string): value is RunView =>
  RUN_VIEWS.some((view) => view === value)
/** A playbook or run opened, or switched to another view, starts with no map step or rubric question open. */
export const CLEARED_DRILL_IN_NAV = {
  mapStep: undefined,
  mapPicked: undefined,
  overviewStep: undefined,
  childNode: undefined,
  mapStepLabel: undefined,
  rubricQuestion: undefined,
  rubricQuestionPrompt: undefined,
} as const

/** Leaving a playbook or run for its list, or opening another, forgets which one was open and everything opened inside it. */
export const CLEARED_PLAYBOOK_DETAIL_NAV = {
  promptId: undefined,
  librarySlug: undefined,
  promptName: undefined,
  promptSlug: undefined,
  draftToolCallId: undefined,
  playbookTab: undefined,
  runId: undefined,
  runView: undefined,
  ...CLEARED_DRILL_IN_NAV,
} as const

/** A map step or rubric question opened, named from the graph and rubric so the viewport can say which. */
export const createDrillInNav = ({
  step,
  question,
  graph,
  rubric,
}: {
  step?: string | null
  question?: string | null
  graph?: PlaybookGraph | null
  rubric?: PlaybookRubric | null
}): Pick<
  TabNavState,
  'mapStep' | 'mapStepLabel' | 'rubricQuestion' | 'rubricQuestionPrompt'
> => ({
  mapStep: step ?? undefined,
  mapStepLabel: step
    ? graph?.nodes.find((node) => node.id === step)?.label
    : undefined,
  rubricQuestion: question ?? undefined,
  rubricQuestionPrompt: question
    ? rubric?.criteria.find((criterion) => criterion.id === question)?.question
        .prompt
    : undefined,
})

/** Playbook and run views as the user sees them named. */
const VIEWPORT_PLAYBOOK_TABS = {
  overview: 'overview',
  map: 'plays',
  rubric: 'scorecard',
  usage: 'usage',
  versions: 'versions',
  result: 'overview',
} as const satisfies Record<
  PlaybookTab | RunView,
  NonNullable<ViewportPlaybookDetail['tab']>
>

export function createListNavState(): TabNavState {
  return { view: 'list' }
}

export const clearFileDetailNavState = (): Partial<TabNavState> => ({
  view: 'list',
  fileId: undefined,
  fileNamespace: undefined,
  filePath: undefined,
  fileName: undefined,
  fileType: undefined,
  pdfScrollTop: undefined,
  viewerInitialLocator: undefined,
  viewerInitialHighlightText: undefined,
  viewerInitialLocatorKey: undefined,
})

let viewerLocatorKeySequence = 0

export const createFileDetailNavState = ({
  file,
  namespace,
  path,
  folderId,
  initialPage,
  initialLocator,
  initialHighlightText,
}: {
  // `type` may be unknown at click time (file query still pending); the
  // detail view fetches the file itself, matching the citation-click seam.
  file: Pick<ServerFile, 'id' | 'name'> & { type?: ServerFile['type'] }
  namespace?: FileNamespaceValue | null
  path?: string | null
  folderId?: UUID | null
  initialPage?: number | null
  initialLocator?: string | null
  initialHighlightText?: string | null
}): Partial<TabNavState> => ({
  view: 'detail',
  fileId: file.id,
  fileNamespace: namespace ?? undefined,
  filePath: path ?? undefined,
  fileType: file.type,
  fileName: file.name,
  ...(folderId !== undefined ? { folderId } : {}),
  activeSourceId: null,
  pdfScrollTop: undefined,
  pdfInitialPage: initialPage ?? undefined,
  viewerInitialLocator: initialLocator ?? undefined,
  viewerInitialHighlightText: initialHighlightText ?? undefined,
  // A fresh key per open: re-clicking the same card must re-apply both the
  // locator selection and captured-text highlight, even when their values are
  // unchanged (the user may have deselected or navigated elsewhere between
  // clicks).
  // No locator means no reveal: the key clears rather than surviving the
  // merge, so a stale key cannot keep a retired reveal (and the chip armed
  // on it) looking alive on a tab now showing something else.
  ...(initialLocator != null || initialHighlightText != null
    ? { viewerInitialLocatorKey: ++viewerLocatorKeySequence }
    : { viewerInitialLocatorKey: undefined }),
})

type MemoryNavItem = Pick<
  MemoryExcerpt,
  | 'id'
  | 'organization_id'
  | 'pursuit_id'
  | 'file_id'
  | 'scope_kind'
  | 'title'
  | 'content'
>

export const createMemoryDetailNavState = (
  memory: MemoryNavItem,
): Partial<TabNavState> => ({
  view: 'detail',
  promptId: memory.id,
  promptName: memory.title?.trim() || memory.content,
  memoryTarget: getMemoryTargetFromExcerpt(memory),
  memoryTab: undefined,
  activeSourceId: null,
})

export const clearMemoryDetailNavState = (): Partial<TabNavState> => ({
  view: 'list',
  promptId: undefined,
  promptName: undefined,
  memoryTarget: undefined,
  memoryTab: undefined,
})

export type PanelViewerState =
  | {
      fileId: UUID
      kind: 'pdf'
      page?: number
      pageCount?: number
      selectedText?: string
      selectionStartPage?: number
      selectionEndPage?: number
      /**
       * Exact highlight boxes measured off the live selection, page-local
       * scale-1 units. Never sent over the wire — baked into the selection
       * card's `pdf:bbox` locator (see selectionLocator.ts).
       */
      selectionPageRects?: readonly PdfSelectionPageRects[]
    }
  | {
      fileId: UUID
      kind: 'spreadsheet'
      sheetName?: string
      selection?: SpreadsheetSelectionState
    }
  | {
      fileId: UUID
      kind: 'presentation'
      aspectRatioLabel?: string
      slideIndex?: number
      slideCount?: number
      outline?: readonly PresentationOutlineFragmentState[]
      selectedObjects?: readonly PresentationObjectState[]
      selectedObjectContext?: PresentationObjectContextState
      selectedText?: string
    }
  | {
      fileId: UUID
      kind: 'text'
      selectedText?: string
    }
  | {
      fileId: UUID
      kind: 'docx'
      page?: number
      pageCount?: number
      selectedText?: string
      /** Word w14:paraId of the paragraph the selection starts in. */
      selectionParagraphId?: string
    }

type SpreadsheetSelectionState =
  | {
      kind: 'range'
      sheetName: string
      selectionKind: 'all' | 'cell' | 'column' | 'row'
      range: {
        startRow: number
        startColumn: number
        endRow: number
        endColumn: number
      }
    }
  | {
      kind: 'shape'
      sheetName: string
      nodeKind: string
    }

interface PresentationObjectState {
  nodeKind: string
  slideIndex: number
  text?: string
  region?: PresentationObjectSelection['region']
  role?: PresentationObjectSelection['role']
}

interface PresentationOutlineFragmentState {
  role?: PresentationObjectSelection['role']
  text: string
}

// Server-side group/text caps mirrored from the backend viewport context
// model; counts stay exact, only groups and text samples are bounded.
const SELECTED_OBJECT_GROUPS_MAX = 20
const SELECTED_OBJECT_TEXTS_MAX = 10
const OUTLINE_FRAGMENTS_MAX = 6
const OUTLINE_FRAGMENT_TEXT_MAX_CHARS = 80
const TABLE_CELLS_MAX = 12
const CHART_SERIES_MAX = 3
const CHART_POINTS_MAX = 8

/**
 * Aggregate a marquee sweep into exact-count groups keyed by
 * (kind, role, slide, region) so the wire payload stays flat no matter how
 * many objects the user grabs; text snippets ride along as bounded samples.
 */
const groupSelectedObjects = (
  objects: readonly PresentationObjectState[],
): PresentationObjectSelection[] => {
  const drafts = new Map<
    string,
    { group: PresentationObjectSelection; candidateTexts: string[] }
  >()
  for (const object of objects) {
    const key = `${object.nodeKind}\u0000${object.role ?? ''}\u0000${object.slideIndex}\u0000${object.region ?? ''}`
    let draft = drafts.get(key)
    if (!draft) {
      draft = {
        group: {
          node_kind: object.nodeKind,
          slide_number: object.slideIndex + 1,
          ...(object.region !== undefined ? { region: object.region } : {}),
          ...(object.role !== undefined ? { role: object.role } : {}),
          count: 0,
        },
        candidateTexts: [],
      }
      drafts.set(key, draft)
    }
    draft.group.count = (draft.group.count ?? 0) + 1
    if (
      object.text !== undefined &&
      draft.candidateTexts.length < SELECTED_OBJECT_TEXTS_MAX
    ) {
      draft.candidateTexts.push(object.text)
    }
  }
  // Sample texts only from the groups that survive the cap; otherwise a
  // dropped group's samples would spend the budget yet never reach the wire.
  const kept = [...drafts.values()].slice(0, SELECTED_OBJECT_GROUPS_MAX)
  let remaining = SELECTED_OBJECT_TEXTS_MAX
  for (const draft of kept) {
    if (remaining === 0) break
    const texts = draft.candidateTexts.slice(0, remaining)
    if (texts.length > 0) {
      draft.group.texts = texts
      remaining -= texts.length
    }
  }
  return kept.map((draft) => draft.group)
}

/**
 * The same viewer state with any user selection removed — what dismissing
 * the composer selection chip leaves behind. Location context (page, sheet,
 * slide) is intentionally kept; only the highlighted content stops
 * traveling with the next message.
 */
export const stripViewerSelection = (
  state: PanelViewerState,
): PanelViewerState => {
  switch (state.kind) {
    case 'pdf': {
      const { selectedText, selectionStartPage, selectionEndPage, ...rest } =
        state
      return rest
    }
    case 'spreadsheet': {
      const { selection, ...rest } = state
      return rest
    }
    case 'presentation': {
      const { selectedObjects, selectedObjectContext, selectedText, ...rest } =
        state
      return rest
    }
    case 'text': {
      const { selectedText, ...rest } = state
      return rest
    }
    case 'docx': {
      const { selectedText, selectionParagraphId, ...rest } = state
      return rest
    }
  }
}

const limitViewportSelectedText = (value: string | undefined) => {
  if (value === undefined || value.length <= 2000) return value
  return `${value.slice(0, 1999)}…`
}

const limitViewportObjectText = (value: string) =>
  value.length > OUTLINE_FRAGMENT_TEXT_MAX_CHARS
    ? `${value.slice(0, OUTLINE_FRAGMENT_TEXT_MAX_CHARS - 1)}…`
    : value

const spreadsheetColumnName = (column: number): string => {
  let remaining = column
  let result = ''
  while (remaining > 0) {
    const remainder = (remaining - 1) % 26
    result = String.fromCharCode(65 + remainder) + result
    remaining = Math.floor((remaining - 1) / 26)
  }
  return result
}

const mapSpreadsheetSelection = (
  selection: SpreadsheetSelectionState | undefined,
): Extract<ViewportViewerState, { kind: 'spreadsheet' }>['selection'] => {
  if (!selection) return undefined
  if (selection.kind === 'shape') {
    return {
      kind: 'shape',
      node_kind: selection.nodeKind,
    }
  }

  const { startRow, startColumn, endRow, endColumn } = selection.range
  const start = `${spreadsheetColumnName(startColumn)}${startRow}`
  const end = `${spreadsheetColumnName(endColumn)}${endRow}`
  return {
    kind: 'range',
    selection_kind: selection.selectionKind,
    a1: start === end ? start : `${start}:${end}`,
    start_row: startRow,
    start_column: startColumn,
    end_row: endRow,
    end_column: endColumn,
  }
}

const mapPresentationOutline = (
  outline: readonly PresentationOutlineFragmentState[],
): PresentationOutlineFragmentWire[] =>
  outline.slice(0, OUTLINE_FRAGMENTS_MAX).flatMap((fragment) => {
    const text = limitViewportObjectText(fragment.text)
    return text
      ? [
          {
            text,
            ...(fragment.role !== undefined ? { role: fragment.role } : {}),
          },
        ]
      : []
  })

const mapPresentationObjectContext = (
  context: PresentationObjectContextState,
): PresentationObjectContextWire =>
  context.kind === 'table'
    ? {
        kind: 'table',
        rows: context.rows,
        columns: context.columns,
        cells: context.cells.slice(0, TABLE_CELLS_MAX).map((cell) => ({
          row: cell.row,
          column: cell.column,
          text: limitViewportObjectText(cell.text),
        })),
      }
    : {
        kind: 'chart',
        chart_kind: context.chartKind,
        ...(context.title
          ? { title: limitViewportObjectText(context.title) }
          : {}),
        ...(context.categoryAxisTitle
          ? {
              category_axis: limitViewportObjectText(context.categoryAxisTitle),
            }
          : {}),
        ...(context.valueAxisTitle
          ? { value_axis: limitViewportObjectText(context.valueAxisTitle) }
          : {}),
        series: context.series.slice(0, CHART_SERIES_MAX).map((series) => ({
          ...(series.name
            ? { name: limitViewportObjectText(series.name) }
            : {}),
          categories: series.categories
            .slice(0, CHART_POINTS_MAX)
            .map(limitViewportObjectText),
          values: series.values.slice(0, CHART_POINTS_MAX),
        })),
      }

const getViewerStateForFile = (
  viewerState: PanelViewerState | undefined,
  fileId: UUID,
): ViewportViewerState | undefined => {
  if (!viewerState || viewerState.fileId !== fileId) return undefined
  switch (viewerState.kind) {
    case 'pdf':
      return {
        kind: 'pdf',
        ...(viewerState.page !== undefined ? { page: viewerState.page } : {}),
        ...(viewerState.pageCount !== undefined
          ? { page_count: viewerState.pageCount }
          : {}),
        ...(viewerState.selectedText !== undefined
          ? {
              selected_text: limitViewportSelectedText(
                viewerState.selectedText,
              ),
            }
          : {}),
        ...(viewerState.selectionStartPage !== undefined
          ? { selection_start_page: viewerState.selectionStartPage }
          : {}),
        ...(viewerState.selectionEndPage !== undefined
          ? { selection_end_page: viewerState.selectionEndPage }
          : {}),
      }
    case 'spreadsheet': {
      const sheetName =
        viewerState.selection?.sheetName ?? viewerState.sheetName
      return {
        kind: 'spreadsheet',
        ...(sheetName ? { sheet_name: sheetName } : {}),
        ...(viewerState.selection
          ? { selection: mapSpreadsheetSelection(viewerState.selection) }
          : {}),
      }
    }
    case 'presentation':
      return {
        kind: 'presentation',
        ...(viewerState.aspectRatioLabel
          ? { aspect: viewerState.aspectRatioLabel }
          : {}),
        ...(viewerState.slideIndex !== undefined
          ? { slide_number: viewerState.slideIndex + 1 }
          : {}),
        ...(viewerState.slideCount !== undefined
          ? { slide_count: viewerState.slideCount }
          : {}),
        ...(viewerState.outline && viewerState.outline.length > 0
          ? { outline: mapPresentationOutline(viewerState.outline) }
          : {}),
        ...(viewerState.selectedObjects &&
        viewerState.selectedObjects.length > 0
          ? {
              selected_objects: groupSelectedObjects(
                viewerState.selectedObjects,
              ),
            }
          : {}),
        ...(viewerState.selectedObjectContext
          ? {
              selected_object_context: mapPresentationObjectContext(
                viewerState.selectedObjectContext,
              ),
            }
          : {}),
        ...(viewerState.selectedText !== undefined
          ? {
              selected_text: limitViewportSelectedText(
                viewerState.selectedText,
              ),
            }
          : {}),
      }
    case 'text':
      return {
        kind: 'text',
        ...(viewerState.selectedText !== undefined
          ? {
              selected_text: limitViewportSelectedText(
                viewerState.selectedText,
              ),
            }
          : {}),
      }
    case 'docx':
      return {
        kind: 'docx',
        ...(viewerState.page !== undefined ? { page: viewerState.page } : {}),
        ...(viewerState.pageCount !== undefined
          ? { page_count: viewerState.pageCount }
          : {}),
        ...(viewerState.selectedText !== undefined
          ? {
              selected_text: limitViewportSelectedText(
                viewerState.selectedText,
              ),
            }
          : {}),
        ...(viewerState.selectionParagraphId !== undefined
          ? { selection_paragraph_id: viewerState.selectionParagraphId }
          : {}),
      }
  }
}

export const getViewportContextFromPanel = ({
  tab,
  tabs = [],
  sourceViewerOpen,
  viewerState,
}: {
  tab: PanelTab | undefined
  /** Every tab open in the panel, in bar order. */
  tabs?: PanelTab[]
  sourceViewerOpen: boolean
  viewerState?: PanelViewerState
}): ViewportContext | null => {
  if (!sourceViewerOpen || !tab) {
    return null
  }

  const { nav } = tab
  const openTabs = tabs.map((panelTab) => panelTab.entrypointId)
  const context: ViewportContext = {
    entrypoint: tab.entrypointId,
    view: nav.view,
    ...(openTabs.length > 0 ? { tabs: openTabs } : {}),
  }

  if (nav.view !== 'detail') return context

  if (nav.fileId && nav.fileType) {
    const matchingViewerState = getViewerStateForFile(viewerState, nav.fileId)
    return {
      ...context,
      detail: {
        kind: 'file',
        type: nav.fileType,
        ...(nav.fileName ? { name: nav.fileName } : {}),
        ...(nav.fileNamespace ? { namespace: nav.fileNamespace } : {}),
        ...(nav.filePath ? { path: nav.filePath } : {}),
        ...(matchingViewerState ? { viewer: matchingViewerState } : {}),
      },
    }
  }

  if (tab.entrypointId === 'memories') {
    const scope =
      nav.memoryTarget?.scope_kind === 'organization' ||
      nav.memoryTarget?.scope_kind === 'pursuit'
        ? nav.memoryTarget.scope_kind
        : nav.memoryListScope
    return {
      ...context,
      detail: {
        kind: 'memory',
        ...(nav.promptName ? { name: nav.promptName } : {}),
        ...(scope ? { scope } : {}),
      },
    }
  }

  if (
    tab.entrypointId === 'playbooks' ||
    tab.entrypointId === 'playbook_runs'
  ) {
    const view =
      tab.entrypointId === 'playbooks'
        ? (nav.playbookTab ?? 'overview')
        : (nav.runView ?? 'result')
    return {
      ...context,
      detail: {
        kind: 'playbook',
        ...(nav.promptName ? { name: nav.promptName } : {}),
        ...(nav.promptSlug ? { slug: nav.promptSlug } : {}),
        ...(tab.entrypointId === 'playbook_runs' && nav.runId
          ? { run_id: nav.runId }
          : {}),
        tab: VIEWPORT_PLAYBOOK_TABS[view],
        ...(nav.mapStep
          ? {
              step_id: nav.mapStep,
              ...(nav.mapStepLabel ? { step_label: nav.mapStepLabel } : {}),
            }
          : {}),
        ...(nav.rubricQuestion
          ? {
              question_id: nav.rubricQuestion,
              ...(nav.rubricQuestionPrompt
                ? { question: nav.rubricQuestionPrompt }
                : {}),
            }
          : {}),
      },
    }
  }

  return context
}

/**
 * Location-only copy of a viewport context: viewer selections (highlighted
 * text, cell ranges, picked objects) are dropped, page/sheet/slide context
 * kept. Selections reach the agent only through an explicit "Add to Chat"
 * card inline in the user message; the viewport reminder must not duplicate
 * (or worse, contradict) what the card captured.
 */
export const stripViewportContextSelection = (
  context: ViewportContext | null,
): ViewportContext | null => {
  if (context?.detail?.kind !== 'file' || !context.detail.viewer) {
    return context
  }
  const viewer = context.detail.viewer
  let stripped: ViewportViewerState
  switch (viewer.kind) {
    case 'pdf': {
      const {
        selected_text,
        selection_start_page,
        selection_end_page,
        ...rest
      } = viewer
      stripped = rest
      break
    }
    case 'spreadsheet': {
      const { selection, ...rest } = viewer
      stripped = rest
      break
    }
    case 'presentation': {
      const {
        selected_objects,
        selected_object_context,
        selected_text,
        ...rest
      } = viewer
      stripped = rest
      break
    }
    case 'text': {
      const { selected_text, ...rest } = viewer
      stripped = rest
      break
    }
    case 'docx': {
      const { selected_text, selection_paragraph_id, ...rest } = viewer
      stripped = rest
      break
    }
  }
  return { ...context, detail: { ...context.detail, viewer: stripped } }
}

export const describePresentationSlide = (
  viewer: PresentationViewerState,
): string | undefined => {
  const slideNumbers = [
    ...new Set(
      (viewer.selected_objects ?? []).map((group) => group.slide_number),
    ),
  ].sort((left, right) => left - right)
  if (slideNumbers.length === 0 && viewer.slide_number !== undefined) {
    slideNumbers.push(viewer.slide_number)
  }
  if (slideNumbers.length === 0) return undefined
  if (slideNumbers.length === 1) return `Slide ${slideNumbers[0]}`
  if (slideNumbers.length <= 3) return `Slides ${slideNumbers.join(', ')}`
  return `Slides ${slideNumbers[0]}–${slideNumbers.at(-1)}`
}

/**
 * Human-readable summary of what is highlighted inside the active viewport,
 * or null when nothing is selected. Drives the composer chip telling the user
 * their selection travels with the next message. Derived from the same
 * `ViewportContext` that `makeSendMessageParams` sends, so the indicator can
 * never disagree with the payload.
 *
 * `fileName` and `label` are split so the chip can truncate the (long) file
 * name independently while keeping the selection label fully visible.
 */
export interface ViewportSelectionSummary {
  fileName: string | null
  label: string
}

export const getViewportSelectionSummary = (
  context: ViewportContext | null,
): ViewportSelectionSummary | null => {
  if (context?.detail?.kind !== 'file') return null
  const { viewer } = context.detail
  if (!viewer) return null
  const fileName = context.detail.name ?? null

  switch (viewer.kind) {
    case 'pdf': {
      if (!viewer.selected_text) return null
      const start = viewer.selection_start_page ?? viewer.page
      const end = viewer.selection_end_page ?? start
      if (start === undefined) return { fileName, label: 'Selection' }
      const pages =
        end !== undefined && end !== start
          ? `Pages ${start}–${end}`
          : `Page ${start}`
      return { fileName, label: pages }
    }
    case 'spreadsheet': {
      const selection = viewer.selection
      if (!selection) return null
      const sheet = viewer.sheet_name ? `${viewer.sheet_name}: ` : ''
      if (selection.kind === 'shape') {
        const nodeKind =
          selection.node_kind.charAt(0).toUpperCase() +
          selection.node_kind.slice(1)
        return { fileName, label: `${sheet}${nodeKind}` }
      }
      return { fileName, label: `${sheet}${selection.a1}` }
    }
    case 'presentation': {
      const objectCount = (viewer.selected_objects ?? []).reduce(
        (total, group) => total + (group.count ?? 1),
        0,
      )
      if (!viewer.selected_text && objectCount === 0) return null
      const slide = describePresentationSlide(viewer)
      const what = viewer.selected_text
        ? 'text'
        : objectCount === 1
          ? '1 object'
          : `${objectCount} objects`
      const label = [slide, what].filter(Boolean).join(', ')
      return {
        fileName,
        label: label.charAt(0).toUpperCase() + label.slice(1),
      }
    }
    case 'text': {
      if (!viewer.selected_text) return null
      return { fileName, label: 'Selection' }
    }
    case 'docx': {
      if (!viewer.selected_text) return null
      return {
        fileName,
        label: viewer.page !== undefined ? `Page ${viewer.page}` : 'Selection',
      }
    }
  }
}

// MARK: Panel tab

export type EntrypointId =
  | 'home'
  | 'pursuit_files'
  | 'opportunity_files'
  | 'organization_files'
  | 'session_files'
  | 'citations'
  | 'playbooks'
  | 'playbook_runs'
  | 'memories'

export interface PanelTab {
  id: string
  entrypointId: EntrypointId
  nav: TabNavState
}

export function createPanelTab(entrypointId: EntrypointId): PanelTab {
  return {
    id: crypto.randomUUID(),
    entrypointId,
    nav: createListNavState(),
  }
}

// MARK: Entrypoint metadata (label + icon, no component reference to avoid circular deps)

export interface EntrypointMeta {
  id: EntrypointId
  label: string
  description: string
  icon: IconType
}

const UNIVERSAL_ENTRYPOINT_ORDER: EntrypointId[] = [
  'home',
  'session_files',
  'pursuit_files',
  'opportunity_files',
  'organization_files',
  'citations',
  'playbooks',
  'playbook_runs',
  'memories',
]

export const ENTRYPOINT_REGISTRY: EntrypointMeta[] = [
  {
    id: 'home',
    label: 'Home',
    description: 'Pick an entry point',
    icon: LuHome,
  },
  {
    id: 'session_files',
    label: 'Chat Files',
    description: 'Browse files generated in this chat',
    icon: LuFileOutput,
  },
  {
    id: 'pursuit_files',
    label: 'Pursuit Files',
    description: 'Browse solicitation files',
    icon: HiOutlineFolderOpen,
  },
  {
    id: 'opportunity_files',
    label: 'Solicitation Files',
    description: 'Browse opportunity solicitation files',
    icon: HiOutlineFolderOpen,
  },
  {
    id: 'organization_files',
    label: 'Library Files',
    description: 'Browse library files',
    icon: HiOutlineBookOpen,
  },
  {
    id: 'citations',
    label: 'Citations',
    description: 'View references used by Sweetspot AI',
    icon: LuQuote,
  },
  {
    id: 'playbooks',
    label: 'Playbooks',
    description: 'View and run playbooks',
    icon: LuBookMarked,
  },
  {
    id: 'playbook_runs',
    label: 'Runs',
    description: 'Follow playbook runs in this chat',
    icon: LuActivity,
  },
  {
    id: 'memories',
    label: 'Memories',
    description: 'Manage reusable AI memory',
    icon: LuStickyNote,
  },
]

const entrypointMetaMap = new Map(ENTRYPOINT_REGISTRY.map((e) => [e.id, e]))
const entrypointIdSet = new Set<EntrypointId>(
  ENTRYPOINT_REGISTRY.map((entrypoint) => entrypoint.id),
)

export const isEntrypointId = (value: unknown): value is EntrypointId => {
  return typeof value === 'string' && entrypointIdSet.has(value as EntrypointId)
}

export function getEntrypointMeta(
  id: EntrypointId,
): EntrypointMeta | undefined {
  return entrypointMetaMap.get(id)
}

export const getFilesEntrypointForAgent = (
  agentName: AgentName,
): EntrypointId => {
  if (agentName === 'organization_chat') {
    return 'organization_files'
  }
  if (agentName === 'opportunity_chat') {
    return 'opportunity_files'
  }
  return 'pursuit_files'
}

// Which entrypoint tab shows a given file's detail view. Library files open
// the library tab; everything else opens the agent's own files entrypoint,
// which is always in the agent's allowed tab set — a hardcoded
// 'pursuit_files' would render as the new-tab menu in organization chat.
// `fileType` may be undefined while the file query is still pending, so the
// tool-result namespace takes precedence when it is available.
export const getFileDetailEntrypoint = (
  agentName: AgentName,
  fileType: FileType | undefined,
  namespace?: FileNamespaceValue | null,
): EntrypointId => {
  if (namespace === 'session' || fileType === 'agent_session_file') {
    return 'session_files'
  }
  if (
    namespace === 'organization' ||
    fileType === 'organization_file' ||
    fileType === 'word_template_file'
  ) {
    return 'organization_files'
  }
  return getFilesEntrypointForAgent(agentName)
}

export interface EntrypointVisibilityArgs {
  agentName: AgentName
  showFilesTab: boolean
}

export const getAllowedEntrypointIds = ({
  agentName,
  showFilesTab,
}: EntrypointVisibilityArgs): EntrypointId[] => {
  const allowed = new Set<EntrypointId>(AGENT_DEFAULT_TABS[agentName] ?? [])

  if (showFilesTab) {
    allowed.add(getFilesEntrypointForAgent(agentName))
    const canAlsoBrowseLibraryFiles =
      agentName === 'opportunity_chat' ||
      agentName === 'pursuit_chat' ||
      agentName === 'pursuit_document_chat'
    if (canAlsoBrowseLibraryFiles) {
      allowed.add('organization_files')
    }
  } else {
    allowed.delete('pursuit_files')
    allowed.delete('opportunity_files')
    allowed.delete('organization_files')
  }

  allowed.add('citations')
  allowed.add('playbooks')
  allowed.add('playbook_runs')
  allowed.add('memories')
  allowed.add('home')
  // Every chat can generate images into its session files, so the Chat
  // Files tab is always available (not gated on code_execution).
  allowed.add('session_files')

  return UNIVERSAL_ENTRYPOINT_ORDER.filter((id) => allowed.has(id))
}

// Citations stay allowed so a tab opened from a citation pill renders and
// shows in the tab bar, but the panel no longer offers citations as something
// the user picks.
const UNPICKABLE_ENTRYPOINTS: ReadonlySet<EntrypointId> = new Set(['citations'])

export const getPickableEntrypointIds = (
  args: EntrypointVisibilityArgs,
): EntrypointId[] =>
  getAllowedEntrypointIds(args).filter((id) => !UNPICKABLE_ENTRYPOINTS.has(id))

// MARK: Default tabs per agent

export const AGENT_DEFAULT_TABS: Record<AgentName, EntrypointId[]> = {
  pursuit_chat: ['pursuit_files'],
  opportunity_chat: ['opportunity_files'],
  organization_chat: ['organization_files'],
  pursuit_document_chat: ['pursuit_files'],
  search_chat: [],
}
